import { isSurveyWorkNewStatus } from './surveyWorkStatus'

export const EQUIPMENT_SERVICES = ['fieldSurvey', 'uniformity']

/** Firestore `surveyWorks.serviceType` written when the equipment is created. */
export const SURVEY_WORK_SERVICE_TYPE = {
  fieldSurvey: 'fieldSurvey',
  uniformity: 'uniformity'
}

export function serviceTypeFromKey (key) {
  return SURVEY_WORK_SERVICE_TYPE[key] || null
}

/** Maps stored `surveyWorks.serviceType` to an equipment service key. */
export function serviceKeyFromServiceType (serviceType) {
  if (serviceType === SURVEY_WORK_SERVICE_TYPE.fieldSurvey) return 'fieldSurvey'
  if (serviceType === SURVEY_WORK_SERVICE_TYPE.uniformity) return 'uniformity'
  return null
}

function nonNegativeWholeNumber (value) {
  if (value == null || value === '') return null
  // Firestore / admin SDK may expose integers as Long-like objects.
  if (typeof value === 'object') {
    if (typeof value.toNumber === 'function') {
      value = value.toNumber()
    } else if (Object.prototype.hasOwnProperty.call(value, 'integerValue')) {
      value = value.integerValue
    } else if (typeof value.low === 'number' && typeof value.high === 'number') {
      if (value.high !== 0 && value.high !== -1) return null
      value = value.low
    }
  }
  const n = typeof value === 'number' ? value : Number(value)
  if (!Number.isFinite(n) || n < 0) return null
  const whole = Math.round(n)
  return Math.abs(n - whole) < 1e-9 ? whole : null
}

function quantityMap (value) {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null
  if (typeof Map !== 'undefined' && value instanceof Map) {
    const obj = {}
    value.forEach((entry, key) => { obj[key] = entry })
    return obj
  }
  return value
}

/**
 * A missing `serviceQuantities` field must never be interpreted as zero services.
 * A present map is the contract: omitted service keys count as zero of that service
 * (HubSpot / writers often skip 0-valued keys). `devicesQty` is not required.
 */
export function serviceQuantities (data) {
  const quantities = quantityMap(data && data.serviceQuantities)
  if (!quantities) return null
  const normalized = {}
  let definedKeys = 0
  for (const key of EQUIPMENT_SERVICES) {
    if (!Object.prototype.hasOwnProperty.call(quantities, key)) {
      normalized[key] = 0
      continue
    }
    const n = nonNegativeWholeNumber(quantities[key])
    if (n === null) return null
    normalized[key] = n
    definedKeys += 1
  }
  return definedKeys === 0 ? null : normalized
}

/** Missing `serviceType` is a pre-uniformity field-survey work. */
export function effectiveServiceKey (work) {
  if (!work) return null
  return serviceKeyFromServiceType(work.serviceType) || 'fieldSurvey'
}

export function assignedServices (work) {
  const key = effectiveServiceKey(work)
  return key ? [key] : []
}

/**
 * Members that would be dropped when syncing a group to `selectedKeys`.
 * Prefers keeping a started work over a new duplicate of the same type.
 */
export function surveyWorksRemovedByServiceSelection (members, selectedKeys) {
  const selected = Array.isArray(selectedKeys) ? selectedKeys : []
  const byType = new Map()
  const extras = []
  ;(members || []).forEach((work) => {
    const key = effectiveServiceKey(work)
    if (!key) return
    if (!byType.has(key)) {
      byType.set(key, work)
      return
    }
    const current = byType.get(key)
    if (isSurveyWorkNewStatus(current.status) && !isSurveyWorkNewStatus(work.status)) {
      extras.push(current)
      byType.set(key, work)
    } else {
      extras.push(work)
    }
  })
  const removed = extras.slice()
  byType.forEach((work, key) => {
    if (!selected.includes(key)) removed.push(work)
  })
  return removed
}

/** Service keys that must stay selected because field work already started. */
export function lockedServiceKeys (members) {
  const keys = []
  ;(members || []).forEach((work) => {
    if (isSurveyWorkNewStatus(work && work.status)) return
    const key = effectiveServiceKey(work)
    if (key && !keys.includes(key)) keys.push(key)
  })
  return keys
}

export function equipmentGroupId (work) {
  if (work && work.equipmentId) return String(work.equipmentId)
  return work && work.id ? String(work.id) : ''
}

export function groupSurveyWorks (works) {
  const buckets = new Map()
  ;(works || []).forEach((work) => {
    const equipmentId = equipmentGroupId(work)
    if (!buckets.has(equipmentId)) buckets.set(equipmentId, [])
    buckets.get(equipmentId).push(work)
  })
  const groups = []
  buckets.forEach((members, equipmentId) => {
    const primary = members[0] || {}
    const serviceKeys = []
    members.forEach((member) => {
      assignedServices(member).forEach((key) => {
        if (!serviceKeys.includes(key)) serviceKeys.push(key)
      })
    })
    serviceKeys.sort((a, b) => EQUIPMENT_SERVICES.indexOf(a) - EQUIPMENT_SERVICES.indexOf(b))
    const chart = members.map((member) => member.pluviometricChart).find((path) => path && String(path).trim()) || ''
    groups.push({
      id: equipmentId,
      equipmentId,
      works: members,
      serviceKeys,
      deviceName: primary.deviceName || '',
      userId: primary.userId || '',
      ponceTechnician: !!primary.ponceTechnician,
      userDisplay: primary.userDisplay || '',
      coordinates: primary.coordinates || '',
      pluviometricChart: chart,
      createdAt: primary.createdAt
    })
  })
  return groups
}

export function serviceRows (quantities, works) {
  if (!quantities) return []
  return EQUIPMENT_SERVICES.map(key => ({
    key,
    contracted: quantities[key],
    assigned: (works || []).filter(work => assignedServices(work).includes(key)).length
  }))
}

export function validServiceSelection (services, quantities, works, editingEquipmentId) {
  if (!quantities) return true
  if (!Array.isArray(services) || !services.length || new Set(services).size !== services.length || services.some(key => !EQUIPMENT_SERVICES.includes(key))) return false
  const others = (works || []).filter(work => equipmentGroupId(work) !== editingEquipmentId)
  return serviceRows(quantities, others)
    .every(row => row.assigned + (services.includes(row.key) ? 1 : 0) <= row.contracted)
}

export function servicesComplete (quantities, works) {
  if (!quantities) return true
  const groups = groupSurveyWorks(works)
  return groups.every(group => group.serviceKeys.length > 0) &&
    serviceRows(quantities, works).every(row => row.assigned === row.contracted)
}

/** Max distinct equipment: one service slot per equipment when there is no overlap. */
export function maxEquipmentSlots (quantities) {
  if (!quantities) return null
  return EQUIPMENT_SERVICES.reduce((sum, key) => sum + quantities[key], 0)
}



// WEBPACK FOOTER //
// ./src/lib/surveyWorkServices.js